import React, { useContext, useState } from 'react';
import { Box, Flex, Spinner, Grid, Text, Button, Image, Icon, useColorMode, useDisclosure, Badge } from '@chakra-ui/react';
import { useMutation, useQuery } from '@apollo/client';
import { Check, X } from '@phosphor-icons/react';
import moment from 'moment';

import { ACCEPT_IDENTIFICATION, AccessPoint, GET_DROPPOINT, REJECT_IDENTIFICATION } from '../query';
import ChangeAccessPointStatusModal from '../modals/ChangeAccessPointStatus';
import { statusColorScheme } from '../../AccessPoints/List/Card';
import SpinnerContext from '../../../contexts/SpinnerContext';
import UserContext from '../../../contexts/UserContext';

interface AccessPointProps extends AccessPoint {
  refetch: () => void;
  dropPointId: string;
}

const AccessPointVerificationTab = ({
  id,
  accessPointID,
  status,
  submitTimestamp,
  identificationSuccess,
  identificationTries,
  identificationSuccessTimestamp,
  rejectionReason,
  rejectionTimestamp,
  createdAt,
  updatedAt,
  openingHours,
  idFront,
  idBack,
  idSelfie,
  refetch,
  dropPointId,
}: AccessPointProps) => {
  const { toggleSpinner } = useContext(SpinnerContext);
  const { user } = useContext(UserContext);
  const [viewing, setViewing] = useState<string | null>(null);
  const [idFrontStatus, setIdFrontStatus] = useState<string>('pending');
  const [idBackStatus, setIdBackStatus] = useState<string>('pending');
  const [idSelfieStatus, setIdSelfieStatus] = useState<string>('pending');
  const [acceptIdentification] = useMutation(ACCEPT_IDENTIFICATION);
  const [rejectIdentification] = useMutation(REJECT_IDENTIFICATION);
  const { colorMode } = useColorMode();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const isAnyStatusRejected = idFrontStatus === 'rejected' || idBackStatus === 'rejected' || idSelfieStatus === 'rejected';
  const isAllStatusApproved = idFrontStatus === 'approved' && idBackStatus === 'approved' && idSelfieStatus === 'approved';
  const alreadyVerified = (identificationSuccessTimestamp || rejectionTimestamp) && status !== 'PENDING';

  const handleAcceptIdentification = async () => {
    toggleSpinner(true);
    try {
      await acceptIdentification({ variables: { internalUserId: user.id, dropPointId } });
      refetch();
    } catch (error) {
      console.log(error);
    } finally {
      toggleSpinner(false);
    }
  };

  const handleRejectIdentification = async () => {
    toggleSpinner(true);
    try {
      await rejectIdentification({ variables: { internalUserId: user.id, dropPointId } });
      refetch();
    } catch (error) {
      console.log(error);
    } finally {
      toggleSpinner(false);
    }
  };

  return (
    <Flex borderLeftWidth="1px" borderRightWidth="1px" borderBottomWidth="1px" px="4" py="8">
      <Box w="100px">
        <Flex
          position="relative"
          justifyContent="center"
          alignItems="center"
          mb="4"
          borderRadius="md"
          overflow="hidden"
          w="80px"
          h="80px"
          border="1px solid"
          borderColor="gray.200"
          cursor="pointer"
          onClick={() => {
            setViewing('idFront');
          }}
        >
          {idFrontStatus === 'approved' && (
            <Flex position="absolute" top="0" left="0" w="100%" h="100%" bg="green.500" justifyContent="center" alignItems="center">
              <Icon as={Check} color="white" fontSize="24" />
            </Flex>
          )}
          {idFrontStatus === 'rejected' && (
            <Flex position="absolute" top="0" left="0" w="100%" h="100%" bg="red.500" justifyContent="center" alignItems="center">
              <Icon as={X} color="white" fontSize="24" />
            </Flex>
          )}
          <Image src={idFront.uri} w="80px" h="80px" objectFit="cover" />
        </Flex>
        <Flex
          position="relative"
          justifyContent="center"
          alignItems="center"
          mb="4"
          borderRadius="md"
          overflow="hidden"
          w="80px"
          h="80px"
          border="1px solid"
          borderColor="gray.200"
          cursor="pointer"
          onClick={() => {
            setViewing('idBack');
          }}
        >
          {idBackStatus === 'approved' && (
            <Flex position="absolute" top="0" left="0" w="100%" h="100%" bg="green.500" justifyContent="center" alignItems="center">
              <Icon as={Check} color="white" fontSize="24" />
            </Flex>
          )}
          {idBackStatus === 'rejected' && (
            <Flex position="absolute" top="0" left="0" w="100%" h="100%" bg="red.500" justifyContent="center" alignItems="center">
              <Icon as={X} color="white" fontSize="24" />
            </Flex>
          )}
          <Image src={idBack.uri} w="80px" h="80px" objectFit="cover" />
        </Flex>
        <Flex
          position="relative"
          justifyContent="center"
          alignItems="center"
          mb="4"
          borderRadius="md"
          overflow="hidden"
          w="80px"
          h="80px"
          border="1px solid"
          borderColor="gray.200"
          cursor="pointer"
          onClick={() => {
            setViewing('idSelfie');
          }}
        >
          {idSelfieStatus === 'approved' && (
            <Flex position="absolute" top="0" left="0" w="100%" h="100%" bg="green.500" justifyContent="center" alignItems="center">
              <Icon as={Check} color="white" fontSize="24" />
            </Flex>
          )}
          {idSelfieStatus === 'rejected' && (
            <Flex position="absolute" top="0" left="0" w="100%" h="100%" bg="red.500" justifyContent="center" alignItems="center">
              <Icon as={X} color="white" fontSize="24" />
            </Flex>
          )}
          <Image src={idSelfie.uri} w="80px" h="80px" objectFit="cover" />
        </Flex>
      </Box>
      <Flex flex="1" flexDirection="column" alignItems="flex-start" gap="2">
        {viewing ? (
          <>
            <Text fontWeight="bold">{viewing === 'idFront' ? 'Vorderseite' : viewing === 'idBack' ? 'Rückseite' : 'Selfie'}</Text>
            {!alreadyVerified ? (
              <>
                <Text>
                  Bitte überprüfe{' '}
                  {viewing === 'idFront'
                    ? 'die Vorderseite des Dokuments'
                    : viewing === 'idBack'
                      ? 'die Rückseite des Dokuments'
                      : 'das Selfie'}
                  .
                </Text>
                <Flex gap="2" mt="4">
                  <Button
                    size="sm"
                    colorScheme="green"
                    onClick={() => {
                      if (viewing === 'idFront') {
                        setIdFrontStatus('approved');
                      } else if (viewing === 'idBack') {
                        setIdBackStatus('approved');
                      } else {
                        setIdSelfieStatus('approved');
                      }
                      setViewing(null);
                    }}
                  >
                    Bestätigen
                  </Button>
                  <Button
                    size="sm"
                    colorScheme="red"
                    onClick={() => {
                      if (viewing === 'idFront') {
                        setIdFrontStatus('rejected');
                      } else if (viewing === 'idBack') {
                        setIdBackStatus('rejected');
                      } else {
                        setIdSelfieStatus('rejected');
                      }
                      setViewing(null);
                    }}
                  >
                    Ablehnen
                  </Button>
                  <Button size="sm" variant="outline" colorScheme="red" onClick={() => setViewing(null)}>
                    Abbrechen
                  </Button>
                </Flex>
              </>
            ) : null}
            <Image src={viewing === 'idFront' ? idFront.uri : viewing === 'idBack' ? idBack.uri : idSelfie.uri} w="100%" mt="4" />
          </>
        ) : alreadyVerified ? (
          <>
            <Text fontWeight="bold">Access Point Verifizierung abgeschlossen</Text>
            <Text>Du kannst die Dokumente weiterhin einsehen.</Text>
          </>
        ) : (
          <>
            <Box mb="4">
              <Text fontWeight="bold">Access Point Verifizierung</Text>
              {isAnyStatusRejected ? (
                <Text color="red.500">Mindestens ein Dokument wurde abgelehnt.</Text>
              ) : isAllStatusApproved ? (
                <Text color="green.500">Alle Dokumente wurden bestätigt.</Text>
              ) : (
                <Text>Bitte überprüfe die folgenden Dokumente und bestätige die Verifizierung.</Text>
              )}
            </Box>
            {isAnyStatusRejected ? (
              <Button size="sm" colorScheme="red" onClick={handleRejectIdentification}>
                Access Point ablehnen
              </Button>
            ) : isAllStatusApproved ? (
              <>
                <Text>Der Access Point wird hiermit auf die Warteliste gepackt.</Text>
                <Button size="sm" colorScheme="green" onClick={handleAcceptIdentification}>
                  Access Point verifizieren
                </Button>
              </>
            ) : null}
          </>
        )}
      </Flex>
    </Flex>
  );
};

export default AccessPointVerificationTab;
